import {
  Cartesian2,
  Cartesian3,
  Cesium3DTile,
  Cesium3DTileFeature,
  Ray,
  ScreenSpaceEventHandler,
  Viewer,
} from "cesium";
import CameraPlugin from "./CameraPlugin";
import { AttachablePluginInterface } from "./CesiumWrapper";
import EventEmitter, { EventEmitterInterface } from "./EventEmitter";
import ScreenSpaceEventsPlugin from "./ScreenSpaceEventsPlugin";

export type PositionEvent = {
  position: Cartesian3;
  screenPosition: Cartesian2;
  object?: Cesium3DTileFeature | Cesium3DTile;
};

export default class PositionRayPickerPlugin
  implements AttachablePluginInterface
{
  private _viewer: Viewer;
  private _leftClickEvent = new EventEmitter<PositionEvent>();
  private _moveEvent = new EventEmitter<PositionEvent>();

  get leftClickEvent(): EventEmitterInterface<PositionEvent> {
    return this._leftClickEvent;
  }

  get moveEvent(): EventEmitterInterface<PositionEvent> {
    return this._moveEvent;
  }

  constructor(
    sse: ScreenSpaceEventsPlugin,
    private readonly _camera: CameraPlugin,
  ) {
    this.attachLeftClickPickHandler(sse.leftClickEvent);
    this.attachMovePickHandler(sse.mouseMoveEvent);
  }

  setViewer(viewer: Viewer) {
    this._viewer = viewer;
  }

  detachViewer() {
    this._viewer = null;
  }

  private attachLeftClickPickHandler(
    clickEvent: EventEmitterInterface<ScreenSpaceEventHandler.PositionedEvent>,
  ) {
    clickEvent.addHandler((target) => {
      if (!this._viewer || !this._leftClickEvent.hasHandlers) return;
      const { position, object } = this.pick(target.position);
      if (position) {
        this._leftClickEvent.emit({
          position,
          screenPosition: target.position,
          object,
        });
      }
    });
  }

  private attachMovePickHandler(
    moveEvent: EventEmitterInterface<ScreenSpaceEventHandler.MotionEvent>,
  ) {
    moveEvent.addHandler((target) => {
      if (!this._viewer || !this._moveEvent.hasHandlers) return;
      const { position, object } = this.pickForMove(target.endPosition);
      this._moveEvent.emit({
        position,
        screenPosition: target.endPosition,
        object,
      });
    });
  }

  private pickForMove(windowPosition: Cartesian2) {
    // scene.pickFromRay does an offscreen render for every call, which is too
    // expensive for pointer tracking on large tilesets. For hover/drag feedback,
    // prefer the already-rendered depth buffer and fall back to terrain.
    const position = this.pickPositionFromDepth(windowPosition);
    if (this._camera.isPositionVisible(position)) {
      return { position, object: undefined };
    }

    const ray = this._viewer.camera.getPickRay(windowPosition, new Ray());
    const globePosition = ray
      ? this._viewer.scene.globe.pick(ray, this._viewer.scene)
      : undefined;
    return {
      position: this._camera.isPositionVisible(globePosition)
        ? globePosition
        : undefined,
    };
  }

  private pickPositionFromDepth(windowPosition: Cartesian2) {
    if (!this._viewer.scene.pickPositionSupported) return null;
    try {
      return this._viewer.scene.pickPosition(windowPosition);
    } catch {
      return null;
    }
  }

  private pick(windowPosition: Cartesian2) {
    const objectsToExclude = this._viewer.entities.values?.filter(
      (entity) => entity.properties?.excludeFromPicking,
    );
    const ray = this._viewer.camera.getPickRay(windowPosition, new Ray());
    const pickedFromRay = this._viewer.scene.pickFromRay(
      ray,
      objectsToExclude,
      0.005,
    );

    if (!pickedFromRay?.object) {
      // If ray picking misses a clipped tileset region, the rendered depth
      // buffer can still identify the visible model surface under the cursor.
      // Prefer that before falling back to terrain so clicks don't pass through
      // clipped model floors to the globe.
      const depthPosition = this.pickPositionFromDepth(windowPosition);
      if (this._camera.isPositionVisible(depthPosition)) {
        return { position: depthPosition };
      }

      // Mouse is hovering over terrain
      const globePosition = this._viewer.scene.globe.pick(
        ray,
        this._viewer.scene,
      );
      return {
        position: this._camera.isPositionVisible(globePosition)
          ? globePosition
          : undefined,
      };
    }

    // Mouse is hovering over a tileset. Prefer pickFromRay's hit position and
    // only fall back to pickPosition if Cesium did not return one; pickPosition
    // is comparatively expensive and was previously called on every mouse move.
    const position =
      pickedFromRay.position ?? this.pickPositionFromDepth(windowPosition);

    return {
      position: this._camera.isPositionVisible(position) ? position : undefined,
      object: pickedFromRay.object,
    };
  }
}
